import cn from 'classnames';
import React, {useEffect, useLayoutEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import styles from './Drawer.module.css';

export type DrawerVariant = 'side' | 'fullscreen';

export type DrawerContentBackground = 'muted' | 'white';

export type DrawerProps = {
	open: boolean;
	onClose: () => void;
	children: React.ReactNode;
	title?: React.ReactNode;
	showCloseButton?: boolean;
	titleIcon?: React.ReactNode;
	footerContent?: React.ReactNode;
	variant?: DrawerVariant;
	closeButtonPosition?: 'start' | 'end';
	bodyClassName?: string;
	panelClassName?: string;
	footerClassName?: string;
	titleSize?: 'default' | 'compact';
	/** Фон области контента (body). По умолчанию `muted` (#f5f5f5). */
	contentBackground?: DrawerContentBackground;
};

const ANIMATION_MS = 300;

function getAnimationDurationMs(): number {
	if (typeof window === 'undefined') {
		return ANIMATION_MS;
	}

	return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : ANIMATION_MS;
}

export function Drawer({
	open,
	onClose,
	children,
	title,
	titleIcon,
	showCloseButton = false,
	footerContent,
	variant = 'side',
	closeButtonPosition = 'start',
	bodyClassName,
	panelClassName,
	footerClassName,
	titleSize = 'default',
	contentBackground = 'muted',
}: DrawerProps): React.ReactElement | null {
	const isFullscreen = variant === 'fullscreen';
	const isWhiteContent = contentBackground === 'white';
	const hasHeader = Boolean(showCloseButton || titleIcon || title);
	const [isMounted, setIsMounted] = useState(false);
	const [isVisible, setIsVisible] = useState(false);
	const panelRef = useRef<HTMLDivElement>(null);
	const unmountTimeoutRef = useRef<number | null>(null);

	const clearUnmountTimeout = () => {
		if (unmountTimeoutRef.current != null) {
			window.clearTimeout(unmountTimeoutRef.current);
			unmountTimeoutRef.current = null;
		}
	};

	useEffect(() => {
		if (open) {
			clearUnmountTimeout();
			setIsMounted(true);
		}
	}, [open]);

	useLayoutEffect(() => {
		if (!isMounted) return;

		if (open) {
			setIsVisible(false);

			const animationDurationMs = getAnimationDurationMs();
			if (animationDurationMs === 0) {
				setIsVisible(true);
				return;
			}

			let frame2 = 0;
			const frame1 = window.requestAnimationFrame(() => {
				frame2 = window.requestAnimationFrame(() => {
					setIsVisible(true);
				});
			});

			return () => {
				window.cancelAnimationFrame(frame1);
				window.cancelAnimationFrame(frame2);
			};
		}

		setIsVisible(false);
	}, [isMounted, open]);

	useEffect(() => {
		if (open || !isMounted) return;

		const panel = panelRef.current;
		const animationDurationMs = getAnimationDurationMs();

		const unmount = () => {
			clearUnmountTimeout();
			setIsMounted(false);
		};

		if (!panel || animationDurationMs === 0) {
			unmount();
			return;
		}

		const handleTransitionEnd = (event: TransitionEvent) => {
			if (event.target !== panel) return;
			if (event.propertyName !== 'transform' && event.propertyName !== 'opacity') return;

			panel.removeEventListener('transitionend', handleTransitionEnd);
			unmount();
		};

		panel.addEventListener('transitionend', handleTransitionEnd);
		unmountTimeoutRef.current = window.setTimeout(() => {
			panel.removeEventListener('transitionend', handleTransitionEnd);
			unmount();
		}, animationDurationMs + 50);

		return () => {
			panel.removeEventListener('transitionend', handleTransitionEnd);
			clearUnmountTimeout();
		};
	}, [open, isMounted]);

	useEffect(() => {
		if (!isMounted) return;

		const onKeyDown = (event: KeyboardEvent): void => {
			if (event.key === 'Escape') {
				onClose();
			}
		};

		document.addEventListener('keydown', onKeyDown);

		return () => document.removeEventListener('keydown', onKeyDown);
	}, [isMounted, onClose]);

	useEffect(() => {
		if (!isMounted) return;

		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = 'hidden';

		return () => {
			document.body.style.overflow = previousOverflow;
		};
	}, [isMounted]);

	useEffect(() => clearUnmountTimeout, []);

	if (!isMounted) {
		return null;
	}

	return createPortal(
		<div
			className={cn(styles.overlay, isVisible && styles.overlayVisible, isFullscreen && styles.overlayFullscreen)}
			role="presentation"
			onClick={(event) => {
				if (isFullscreen) {
					return;
				}

				if (event.target === event.currentTarget) {
					onClose();
				}
			}}
		>
			<div
				ref={panelRef}
				className={cn(styles.panel, isVisible && styles.panelVisible, isFullscreen && styles.panelFullscreen, panelClassName)}
				role="dialog"
				aria-modal="true"
				onClick={(event) => event.stopPropagation()}
			>
				{hasHeader ? (
					<header
						className={cn(
							styles.header,
							isFullscreen && styles.headerFullscreen,
							closeButtonPosition === 'end' && styles.headerCloseEnd,
						)}
					>
						<div className={styles.titleRow}>
							{closeButtonPosition === 'end' ? (
								<>
									{title ? (
										<h2 className={cn(styles.title, titleSize === 'compact' && styles.titleCompact)}>{title}</h2>
									) : null}
									{titleIcon ? <div className={styles.titleIcon}>{titleIcon}</div> : null}
									{showCloseButton ? (
										<button type="button" onClick={onClose} aria-label="Закрыть" className={styles.closeButton}>
											<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
												<path d="M3.99805 3.99805L20.0004 20.0004" stroke="black" strokeWidth="1.6" />
												<path d="M3.99805 20L20.0004 3.99768" stroke="black" strokeWidth="1.6" />
											</svg>
										</button>
									) : null}
								</>
							) : (
								<>
									{showCloseButton ? (
										<button type="button" onClick={onClose} aria-label="Закрыть" className={styles.closeButton}>
											<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
												<path d="M3.99805 3.99805L20.0004 20.0004" stroke="black" strokeWidth="1.6" />
												<path d="M3.99805 20L20.0004 3.99768" stroke="black" strokeWidth="1.6" />
											</svg>
										</button>
									) : null}
									{titleIcon ? <div className={styles.titleIcon}>{titleIcon}</div> : null}
									{title ? (
										<h2 className={cn(styles.title, titleSize === 'compact' && styles.titleCompact)}>{title}</h2>
									) : null}
								</>
							)}
						</div>
					</header>
				) : null}

				<div
					className={cn(
						styles.body,
						isFullscreen && styles.bodyFullscreen,
						isWhiteContent && styles.bodyWhite,
						bodyClassName,
					)}
				>
					{children}
				</div>
				{footerContent ? (
					<footer className={cn(styles.footer, isFullscreen && styles.footerFullscreen, footerClassName)}>
						{footerContent}
					</footer>
				) : null}
			</div>
		</div>,
		document.body,
	);
}
